import React, { useState } from 'react';
// @ts-ignore
import styles from './ManagerInsuranceReportListItem.module.scss';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import {
	InsuranceReport,
	InsuranceReportState,
	InsuranceReportStateError,
	useDeleteInsuranceReportMutation,
} from '../../../../graphql/graphql';
import InsuranceReportUpdateModal from '../InsuranceReportUpdate/InsuranceReportUpdateModal';
import ManagerInsuranceReportListItemItem from './ManagerInsuranceReportListItemItem';
import { joinClasses } from '../../../../Utils/className.util';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';

interface Props {
	item: InsuranceReport;
	highlightByState?: boolean;
	isSelected?: boolean;
	onSelect?: (value: boolean) => void;
	onDelete: () => void;
	onReload: () => void;
}

export const STATE_TRANSLATIONS: Record<InsuranceReportState, string> = {
	[InsuranceReportState.Unreported]: 'D',
	[InsuranceReportState.Error]: 'C',
	[InsuranceReportState.Reported]: 'V',
	[InsuranceReportState.Cancelled]: 'S',
	[InsuranceReportState.Rejected]: 'O',
	[InsuranceReportState.PartiallyAccepted]: 'Q',
	[InsuranceReportState.Irreparable]: 'N',
};

export const STATE_ERROR_TRANSLATIONS: Record<
	InsuranceReportStateError,
	string
> = {
	[InsuranceReportStateError.Ok]: 'D',
	[InsuranceReportStateError.Error]: 'N',
};

export const formatInsuranceReportNote = (note: string): string =>
	note.replace(/\/n|\\n/g, '\n');

const getHighlightClass = (state?: InsuranceReportState | null): string => {
	if (state === InsuranceReportState.Rejected) {
		return styles.invalid;
	}

	if (state === InsuranceReportState.Unreported) {
		return styles.pending;
	}

	return '';
};

const ManagerInsuranceReportListItem: React.FC<Props> = ({
	item,
	highlightByState,
	isSelected,
	onSelect,
	onDelete,
	onReload,
}) => {
	const [deleteInsuranceReport] = useDeleteInsuranceReportMutation();
	const [isEditOpen, setIsEditOpen] = useState(false);

	const handleRemoveInsuranceReport = async () => {
		await deleteInsuranceReport({ id: item.id });
		onDelete();
	};

	const handleUpdate = () => {
		setIsEditOpen(false);
		onReload();
	};

	return (
		<div
			className={joinClasses([
				styles.wrapper,
				highlightByState ? getHighlightClass(item.state) : '',
			])}>
			{onSelect && (
				<div className={styles.select}>
					<Checkbox value={isSelected} onChange={onSelect} />
				</div>
			)}
			<div className={styles.date}>
				<FormattedMomentDate date={item.reportedAt} format={DateFormat.DATE} />
			</div>
			<div className={styles.content}>
				{item.doctor?.profile?.fullName && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.doctor'
						value={item.doctor?.profile?.fullName}
					/>
				)}
				{item.patient?.profile?.fullName && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.patient'
						value={item.patient.profile.fullName}
					/>
				)}
				{item.insurance?.name && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.insuranceCompany'
						value={item.insurance.name}
					/>
				)}
				{item.diagnosis && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.diagnosis'
						value={item.diagnosis}
					/>
				)}
				{item.operation?.title && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.operation'
						value={item.operation.title}
					/>
				)}
				{item.count && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.count'
						value={item.count}
					/>
				)}
				{item.points && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.points'
						value={item.points}
					/>
				)}
				{item.state && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.state'
						value={STATE_TRANSLATIONS[item.state]}
					/>
				)}
				{item.stateError && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.stateError'
						value={STATE_ERROR_TRANSLATIONS[item.stateError]}
					/>
				)}
				{item.note && (
					<ManagerInsuranceReportListItemItem
						title='component.insuranceReportsListItem.note'
						value={formatInsuranceReportNote(item.note)}
					/>
				)}
			</div>
			<div className={styles.controls}>
				<LinkButton
					title='base.shared.edit'
					theme={Theme.BLUE}
					firstUpper
					onClick={() => setIsEditOpen(true)}
				/>
				<RemoveButton
					title='base.shared.delete'
					onClick={handleRemoveInsuranceReport}
					firstUpper
				/>
			</div>
			{isEditOpen && (
				<InsuranceReportUpdateModal
					insuranceReport={item}
					onUpdate={handleUpdate}
					isOpen
					onClose={() => setIsEditOpen(false)}
				/>
			)}
		</div>
	);
};

export default ManagerInsuranceReportListItem;
